import clsx from "clsx";
import { X } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef } from "react";

const widths = { sm: "w-[min(92vw,420px)]", md: "w-[min(92vw,560px)]", lg: "w-[min(94vw,820px)]" } as const;

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  dismissible = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: keyof typeof widths;
  dismissible?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      // showModal() focuses the first focusable element (the Close button); prefer a field in the body.
      d.querySelector<HTMLElement>(
        "[data-autofocus], [autofocus], input:not([type=hidden]), textarea, select",
      )?.focus();
    }
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className={clsx(
        "m-auto max-h-[88vh] overflow-hidden rounded-xl border border-line bg-surface p-0 text-ink shadow-pop",
        widths[size],
      )}
      onCancel={(e) => {
        e.preventDefault();
        if (dismissible) onClose();
      }}
      onClose={() => {
        if (openRef.current) onClose();
      }}
    >
      {open ? (
        <div className="flex max-h-[88vh] flex-col">
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 pt-4 pb-3">
            <div>
              <h2 id={titleId} className="text-xl font-semibold text-ink">
                {title}
              </h2>
              {description ? <div className="mt-0.5 text-sm text-ink-2">{description}</div> : null}
            </div>
            {dismissible ? (
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="grid h-7 w-7 place-items-center rounded-md text-ink-3 hover:bg-green-wash hover:text-green-ink"
              >
                <X size={16} />
              </button>
            ) : null}
          </div>
          <div className="overflow-auto px-5 py-4">{children}</div>
          {footer ? (
            <div className="flex justify-end gap-2 border-t border-line bg-column px-5 py-3">{footer}</div>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}
